"use client";

import { useCallback, useEffect, useState } from "react";
import { useAuthStore } from "@/shared/model/auth-store";
import { api } from "@/shared/lib/api";
import { useVisibleInterval } from "@/shared/lib/hooks/use-visible-interval";

const ANON_ID_KEY = "gl_anon_id";
const HEARTBEAT_MS = 60_000;

function getAnonId(): string {
  try {
    let id = localStorage.getItem(ANON_ID_KEY);
    if (!id) {
      id =
        (globalThis.crypto?.randomUUID?.() ?? `a${Date.now()}${Math.floor(Math.random() * 1e6)}`)
          .replace(/-/g, "")
          .slice(0, 32);
      localStorage.setItem(ANON_ID_KEY, id);
    }
    return id;
  } catch {
    return "";
  }
}

/**
 * Heartbeat анонимного (незалогиненного) посетителя — для метрики «сколько людей
 * на сайте без входа» в админ-статистике. Шлёт раз в 60с свой стабильный anonId,
 * только пока пользователь НЕ авторизован. Бэкенд держит ключ с TTL 90с.
 */
export function AnonPresence() {
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [anonId, setAnonId] = useState("");

  const enabled = isInitialized && !isAuthenticated;

  const ping = useCallback(() => {
    if (!anonId) return;
    api.post("/stats/anon-heartbeat", { id: anonId }).catch(() => {});
  }, [anonId]);

  useEffect(() => {
    if (!enabled) {
      setAnonId("");
      return;
    }
    setAnonId(getAnonId());
  }, [enabled]);

  // Первый пинг — сразу: это и есть событие «посетитель пришёл».
  useEffect(() => {
    if (!enabled || !anonId) return;
    ping();
  }, [enabled, anonId, ping]);

  // KAN-935: в свёрнутой вкладке присутствия нет. Метрика меряет «сколько людей
  // на сайте», а забытая вкладка сообщала «я тут» часами — при 500 зрителях со
  // стрима это 8,3 запроса в секунду чистой телеметрии ни за чем.
  useVisibleInterval(ping, enabled && anonId ? HEARTBEAT_MS : null);

  return null;
}
